// Alpine.js
import Alpine from 'alpinejs'

// Fancybox
import { Fancybox } from '@fancyapps/ui'
import '@fancyapps/ui/dist/fancybox/fancybox.css'

// Styles
import '../css/main.css'

// Initialize Alpine
window.Alpine = Alpine

// Umami event tracking helper
function track(event, data) {
  try { window.umami?.track(event, data) } catch (e) { /* ad blocker */ }
}

// Global instant search component (used in header)
window.siteSearch = function () {
  let _trackTimer = null
  return {
    q: '',
    groups: [],
    loading: false,
    get hasResults() { return this.groups.length > 0 },
    async search() {
      if (this.q.trim().length < 2) {
        this.groups = []
        return
      }
      this.loading = true
      try {
        const resp = await fetch('/api/search?q=' + encodeURIComponent(this.q))
        const data = await resp.json()
        this.groups = data.groups || []
        // Track search after 1s idle (final query, not every keystroke)
        clearTimeout(_trackTimer)
        _trackTimer = setTimeout(() => {
          const total = data.total || 0
          track(total > 0 ? 'search' : 'search-no-results', {
            query: this.q.trim().toLowerCase(),
            results: total,
          })
        }, 1000)
      } catch (e) {
        this.groups = []
      }
      this.loading = false
    },
  }
}

Alpine.data('heroGrid', () => ({
  equalize() {
    const main = this.$refs.main
    const side = this.$refs.side
    if (!main || !side || window.innerWidth < 1024) {
      main?.style.removeProperty('height')
      return
    }
    main.style.height = side.offsetHeight + 'px'
  },
  init() {
    const imgs = this.$el.querySelectorAll('img')
    let loaded = 0
    const total = imgs.length
    const check = () => { if (++loaded >= total) this.equalize() }
    imgs.forEach(img => {
      if (img.complete) check()
      else img.addEventListener('load', check, { once: true })
    })
    window.addEventListener('resize', () => this.equalize())
  }
}))

// --------------------------------------------------------------------------
// Classic-music.ru only: Turbo (SPA-like navigation) + sticky mini-player.
// Gated on `<body data-cm-player>`, so belcanto/operanews stay lean.
// We dynamically import the CM-only bundle, register the Alpine component,
// and only THEN call Alpine.start() — otherwise Alpine would scan the DOM
// before the component is registered and crash on `x-data="cmPlayer"`.
// --------------------------------------------------------------------------
async function boot() {
  const isClassicMusic = document.body?.hasAttribute('data-cm-player')
  if (isClassicMusic) {
    // Turbo auto-starts on import. We keep Turbo Drive enabled so Turbo
    // intercepts all link clicks, and rely on `data-turbo-frame="cm-main"`
    // on individual <a> elements (header, footer, logo) to route their
    // navigation through the main frame instead of a full body swap —
    // which would destroy the sticky audio player.
    const [, playerMod] = await Promise.all([
      import('@hotwired/turbo'),
      import('./cm_player.js'),
    ])
    playerMod.registerCmPlayer()

    // Reset scroll to top on every Turbo frame navigation. Default Turbo
    // Frame behavior preserves the current scroll position after content
    // swap — feels broken on a content site. `turbo:frame-load` fires
    // last in the frame lifecycle chain. The rAF wrapper runs our
    // scrollTo in the next paint frame, guaranteeing it executes AFTER
    // Turbo Drive's own scroll management (which races when the frame
    // has data-turbo-action="advance" and triggers a pseudo-visit).
    document.addEventListener('turbo:frame-load', (event) => {
      if (event.target?.id === 'cm-main') {
        requestAnimationFrame(() => window.scrollTo(0, 0))
      }
    })
  }
  Alpine.start()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot)
} else {
  boot()
}

// Initialize Fancybox
function initFancybox() {
  const fancyboxOptions = {
    Toolbar: {
      display: {
        left: ['infobar'],
        middle: [],
        right: ['close'],
      },
    },
    Images: {
      zoom: false,
    },
    on: {
      'Carousel.selectSlide': () => {
        track('gallery-view', { page: window.location.pathname })
      },
    },
  }

  // Bind existing data-fancybox elements
  Fancybox.bind('[data-fancybox]', fancyboxOptions)

  // Handle legacy a.modal links (from admin image uploads)
  document.querySelectorAll('a.modal').forEach((link) => {
    if (!link.hasAttribute('data-fancybox')) {
      link.setAttribute('data-fancybox', 'gallery')
      // Use title as caption if available
      const title = link.getAttribute('title')
      if (title) {
        link.setAttribute('data-caption', title)
      }
    }
  })

  // Bind the newly added fancybox elements
  Fancybox.bind('a.modal[data-fancybox]', fancyboxOptions)
}

// Легаси-картинки из старой админки: <p><a class="modal"><img class="floated_left"></a></p>.
// В нынешней теме у floated_left нет ни одного CSS-правила, поэтому такая картинка
// просто прижата к левому краю колонки и БЕЗ подписи (Иван, 02.08.2026).
// Приводим её к тому же виду, что и <div class="centered"> — дальше её подхватывает
// wrapInlineImages() и вешает figcaption из title/alt. Тела статей в БД не трогаем.
function normalizeLegacyImages() {
  document.querySelectorAll('.prose-opera img.floated_left').forEach((img) => {
    img.classList.remove('floated_left')
    const node = img.closest('a.modal') || img
    if (node.closest('.centered')) return // уже в обёртке — хватило снятия класса
    const parent = node.parentNode
    if (!parent) return
    const div = document.createElement('div')
    div.className = 'centered'
    parent.insertBefore(div, node)
    div.appendChild(node)
    // markdown обернул картинку в отдельный <p> — он больше не нужен и мешает отступам
    if (parent.tagName === 'P' && parent.children.length === 1 && !parent.textContent.trim()) {
      parent.replaceWith(div)
    }
  })
}

// Wrap inline body images with figcaption from title/alt
function wrapInlineImages() {
  document.querySelectorAll('.prose-opera .centered').forEach((div) => {
    const link = div.querySelector('a.modal')
    if (!link) return
    const caption = link.getAttribute('title') || link.querySelector('img')?.getAttribute('alt') || ''
    if (!caption) return
    const figure = document.createElement('figure')
    figure.className = 'my-8'
    div.parentNode.insertBefore(figure, div)
    figure.appendChild(div)
    const figcaption = document.createElement('figcaption')
    figcaption.className = 'text-caption mt-2 text-center italic'
    figcaption.textContent = caption
    figure.appendChild(figcaption)
  })
}

// Handle both cases: DOM already ready or not yet
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { initFancybox(); normalizeLegacyImages(); wrapInlineImages() })
} else {
  initFancybox()
  normalizeLegacyImages()
  wrapInlineImages()
}

// Page counter
;(function () {
  const path = window.location.pathname
  let slug = path.replace(/^\/|\/$/g, '').replace(/\.html$/, '').replace(/\//g, '__')
  if (!slug || slug === '') {
    slug = 'index'
  }
  if (slug) {
    fetch('/counter?slug=' + encodeURIComponent(slug))
      .then((response) => response.json())
      .catch(() => {})
  }
})()
